import { useState, useEffect } from 'react'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import type { PharmacyResponse } from '@/api/types'

export interface ProviderSettingsModalProps {
  open: boolean
  onClose: () => void
  pharmacy: PharmacyResponse | null
  onSave: (data: { npi_required: boolean; dea_required: boolean }) => Promise<void>
  loading?: boolean
}

export function ProviderSettingsModal({
  open,
  onClose,
  pharmacy,
  onSave,
  loading = false,
}: ProviderSettingsModalProps) {
  const [npiRequired, setNpiRequired] = useState(true)
  const [deaRequired, setDeaRequired] = useState(false)

  // Sync state when modal opens or pharmacy changes
  useEffect(() => {
    if (open && pharmacy) {
      setNpiRequired(pharmacy.npi_required)
      setDeaRequired(pharmacy.dea_required)
    }
  }, [open, pharmacy])

  async function handleSave() {
    await onSave({ npi_required: npiRequired, dea_required: deaRequired })
  }

  return (
    <Modal open={open} onClose={onClose} title="Provider Settings" size="sm">
      <div className="space-y-5">
        <p className="text-sm text-charcoal/70">
          Configure which credentials are required for providers to complete their profile.
        </p>

        {/* NPI Required Toggle */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-charcoal">NPI Required</p>
            <p className="text-xs text-gray-500 mt-0.5">
              Require providers to enter their NPI number
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={npiRequired}
            onClick={() => setNpiRequired(!npiRequired)}
            className={`
              relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
              transition-colors duration-200 ease-in-out
              focus:outline-none focus-visible:ring-2 focus-visible:ring-lavender focus-visible:ring-offset-2
              ${npiRequired ? 'bg-deep-purple' : 'bg-gray-300'}
            `}
            disabled={loading}
          >
            <span
              className={`
                pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0
                transition duration-200 ease-in-out
                ${npiRequired ? 'translate-x-5' : 'translate-x-0'}
              `}
            />
          </button>
        </div>

        {/* DEA Required Toggle */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-charcoal">DEA Required</p>
            <p className="text-xs text-gray-500 mt-0.5">
              Require providers to enter their DEA number
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={deaRequired}
            onClick={() => setDeaRequired(!deaRequired)}
            className={`
              relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
              transition-colors duration-200 ease-in-out
              focus:outline-none focus-visible:ring-2 focus-visible:ring-lavender focus-visible:ring-offset-2
              ${deaRequired ? 'bg-deep-purple' : 'bg-gray-300'}
            `}
            disabled={loading}
          >
            <span
              className={`
                pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0
                transition duration-200 ease-in-out
                ${deaRequired ? 'translate-x-5' : 'translate-x-0'}
              `}
            />
          </button>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default ProviderSettingsModal
